import React, { useEffect, useState } from "react";
import {
  TextField,
  MenuItem,
  Button,
  Typography,
  Grid,
  Paper,
} from "@mui/material";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import CommonButton from "./CommonButton";
import TextInput from "./TextInput";
import SelectInput from "./SelectInput";
import {
  errorNotify,
  infoNotify,
  successNotify,
} from "../ToastComponent/ToastNotification";
import { axiosAPI } from "../AxiosComponents/Axios";

const AddLookup = ({
  title,
  sections,
  addDataAPIEndpoint = "",
  editDataAPIEndPoint = "",
  viewDataDetailsAPIEndPoint = "",
  redirectURL,
  validationRules, // New prop for validation
  alert = "",
  submitMethod = "post",
}) => {
  const Navigate = useNavigate();

  const [loader, setLoader] = useState(true);
  const [isDisabled, setIsDisabled] = useState(false);

  const [formData, setFormData] = useState({});

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validate = () => {
    const newErrors = {};
    sections.forEach((section) => {
      section.fields.forEach((field) => {
        const fieldValidation = validationRules?.[field.name];

        if (
          fieldValidation?.required &&
          (formData[field.name] === undefined ||
            formData[field.name] === null ||
            formData[field.name] === "")
        ) {
          newErrors[field.name] = `${field.label} is required.`;
        }

        if (
          fieldValidation?.minLength &&
          formData[field.name]?.length < fieldValidation.minLength
        ) {
          newErrors[
            field.name
          ] = `${field.label} must be at least ${fieldValidation.minLength} characters.`;
        }

        if (
          fieldValidation?.pattern &&
          !new RegExp(fieldValidation.pattern).test(formData[field.name])
        ) {
          newErrors[field.name] = `Invalid ${field.label}.`;
        }

        // You can add more validations like email, number, etc.
      });
    });

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      // Display the errors as toast notifications
      Object.values(newErrors).forEach((error) => {
        errorNotify(error);
      });
      return false;
    }

    return true;
  };

  const getData = () => {
    axiosAPI
      ?.get(viewDataDetailsAPIEndPoint)
      .then((res) => {
        setLoader(false);
        const responseData = res?.data?.data;

        if (responseData) {
          const filteredData = Object.fromEntries(
            Object.entries(responseData).filter(([key]) => !key.endsWith("_Id"))
          );

          setFormData(filteredData);
        }
      })
      .catch((err) => {
        errorNotify("Something went wrong");
      });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setIsDisabled(true);

    const redirectToPage = () => {
      const url = new URL(window.location.origin + redirectURL);
      const queryParams = url.search;
      Navigate({
        pathname: url.pathname,
        search: queryParams,
      });
    };

    try {
      let res;

      if (editDataAPIEndPoint) {
        res = await axiosAPI[submitMethod](editDataAPIEndPoint, formData);
      } else {
        res = await axiosAPI.post(addDataAPIEndpoint, formData);
      }

      if (res?.data?.success) {
        successNotify(res?.data?.message);
        redirectToPage();
      } else {
        infoNotify(res?.data?.message);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      errorNotify("Something went wrong");
    } finally {
      setIsDisabled(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);

    if (editDataAPIEndPoint) {
      getData();
    } else {
      const initialFormData = {};

      sections?.forEach((section) => {
        section.fields.forEach((field) => {
          if (field.label == "Status") {
            initialFormData[field.name] = "1"; // default value
          }
        });
      });

      setFormData(initialFormData);
      setLoader(false);
    }
  }, []);

  return (
    <Paper
      sx={{
        maxWidth: `100vw`,
        padding: 2,
        margin: "auto",
        boxShadow: "none",
        overflowX: "auto",
        fontFamily: "Geist, sans-serif",
        minHeight: "100%",
      }}
    >
      <Typography
        variant="h5"
        sx={{
          fontWeight: 600,
          textAlign: "left",
          marginBottom: 3,
          fontFamily: "Geist, sans-serif",
        }}
      >
        {title}
      </Typography>

      {sections.map((section, index) => (
        <Grid
          container
          sx={{
            border: "1px solid lightgrey",
            borderRadius: "10px",
            padding: "20px",
          }}
          key={index}
        >
          {section?.fields.map((field, fieldIndex) => (
            <>
              {field?.type !== "select" && (
                <Grid
                  item
                  sx={{ padding: "5px" }}
                  xs={12}
                  sm={6}
                  key={fieldIndex}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
                  >
                    {field?.label}{" "}
                    {field?.required ? (
                      <span style={{ color: "red" }}>*</span>
                    ) : null}
                  </Typography>

                  <TextInput
                    name={field?.name}
                    placeholder={field?.label}
                    type={field?.type}
                    value={formData[field?.name] || ""}
                    onChange={handleChange}
                    error={!!errors[field?.name]} // Show error if present
                    loading={loader}
                    maxLength={field?.maxLength ? field?.maxLength : ""}
                  />
                </Grid>
              )}

              {field?.type === "select" && (
                <Grid item sx={{ padding: "5px" }} xs={12} sm={6}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
                  >
                    {field?.label}{" "}
                    {field?.required ? (
                      <span style={{ color: "red" }}>*</span>
                    ) : null}
                  </Typography>

                  <SelectInput
                    placeholder={`Choose ${field?.label}`}
                    name={field?.name}
                    options={field?.options}
                    value={String(formData[field?.name] ?? "")} // Convert to string
                    onChange={handleChange}
                    error={!!errors[field?.name]}
                    loading={loader}
                  />
                </Grid>
              )}
            </>
          ))}
        </Grid>
      ))}

      <Grid container spacing={2} justifyContent="flex-end" sx={{ mt: 3 }}>
        <Grid item>
          <CommonButton
            onClick={() => {
              Navigate(redirectURL);
            }}
            variant="outlined"
            customColor="#B0BEC5"
          >
            Cancel
          </CommonButton>
        </Grid>
        <Grid item>
          <CommonButton
            onClick={handleSubmit}
            variant="contained"
            customColor="#007BFF"
            disabled={isDisabled}
          >
            {isDisabled ? "Saving..." : "Save"}
          </CommonButton>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default AddLookup;
